import React from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { borderRadius } from '../../../theme/theme';

export type WorkspacePanelTone = 'default' | 'primary' | 'success' | 'warning' | 'error';

export interface WorkspacePanelSxOptions {
  tone?: WorkspacePanelTone;
  raised?: boolean;
}

export interface WorkspacePanelProps extends WorkspacePanelSxOptions {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  /** @deprecated use `data-testid` via Box props instead */
  dataTestId?: string;
}

function toneColors(theme: Theme, tone: WorkspacePanelTone) {
  switch (tone) {
    case 'primary':
      return {
        border: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.16 : 0.24),
        background: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.06 : 0.08),
      };
    case 'success':
      return {
        border: alpha(theme.palette.success.main, theme.palette.mode === 'light' ? 0.22 : 0.28),
        background: alpha(theme.palette.success.main, theme.palette.mode === 'light' ? 0.08 : 0.1),
      };
    case 'warning':
      return {
        border: alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.24 : 0.3),
        background: alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.1 : 0.12),
      };
    case 'error':
      return {
        border: alpha(theme.palette.error.main, theme.palette.mode === 'light' ? 0.22 : 0.28),
        background: alpha(theme.palette.error.main, theme.palette.mode === 'light' ? 0.08 : 0.1),
      };
    default:
      return {
        border: alpha(
          theme.palette.divider,
          theme.palette.mode === 'light' ? 0.18 : 0.12
        ),
        background: alpha(
          theme.palette.background.paper,
          theme.palette.mode === 'light' ? 0.86 : 0.28
        ),
      };
  }
}

/**
 * Shared style factory for compact workspace panels/cards.
 *
 * Provides consistent border radius, border, and background across the
 * validation, mapping, and quick-fill workspaces. The `raised` variant uses
 * stronger fill/border values for surfaces that need to sit slightly above
 * their surroundings; `tone` adds semantic color without inventing new visual
 * languages per feature.
 */
export function workspacePanelSx(theme: Theme, options?: WorkspacePanelSxOptions): SxProps<Theme> {
  const { tone = 'default', raised = false } = options ?? {};
  const semantic = toneColors(theme, tone);
  const isLight = theme.palette.mode === 'light';

  const raisedOverride = raised
    ? {
        borderColor: alpha(theme.palette.divider, isLight ? 0.22 : 0.16),
        backgroundColor: alpha(theme.palette.background.paper, isLight ? 0.94 : 0.34),
      }
    : {};

  return {
    borderRadius: `${borderRadius.sm}px`,
    border: `1px solid ${semantic.border}`,
    backgroundColor: semantic.background,
    ...raisedOverride,
  };
}

/**
 * Convenience wrapper around {@link workspacePanelSx}.
 */
export const WorkspacePanel: React.FC<WorkspacePanelProps> = ({
  children,
  sx,
  tone = 'default',
  raised = false,
  dataTestId,
  ...boxProps
}) => {
  const theme = useTheme();
  return (
    <Box
      data-testid={dataTestId}
      sx={[workspacePanelSx(theme, { tone, raised }), ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...boxProps}
    >
      {children}
    </Box>
  );
};

export default WorkspacePanel;
